import { Button } from '@odekoteam/doppio';
import { useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { useFocusTrap } from 'src/hooks/useFocusTrap';
import { hideModal } from 'src/state/slices/modal';
import { MetrobiOffender } from 'src/utils/thirdPartyDelivery';
import { buttonContainer, styledConfirmationModal } from './styles';

export default function MetrobiIneligibleModal(): JSX.Element {
  const {
    mode,
    offenders,
    returnFocusId,
  }: {
    mode: 'move' | 'mark';
    offenders: MetrobiOffender[];
    returnFocusId?: string;
  } = useAppSelector((state) => state.modal.variables);
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const cancel = useCallback(() => {
    dispatch(hideModal());
    // Async actions disable (and blur) their opener before the dialog mounts.
    // Restore the known control as well as the focus trap's synchronous opener.
    if (returnFocusId) document.getElementById(returnFocusId)?.focus();
  }, [dispatch, returnFocusId]);
  useFocusTrap(ref, { isOpen: true, onClose: cancel });
  const singular = offenders.length === 1;
  const title =
    mode === 'mark'
      ? `This route has ${offenders.length} ${
          singular ? 'stop' : 'stops'
        } that can't go to Metrobi`
      : `Can't add ${
          singular ? 'this stop' : 'these stops'
        } to a Metrobi route`;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-labelledby="metrobi-block-title"
      aria-describedby="metrobi-block-description"
      sx={{
        ...styledConfirmationModal,
        justifyContent: 'flex-start',
        maxWidth: mode === 'mark' || !singular ? '420px' : '350px',
        maxHeight: '80vh',
        overflowY: 'auto',
      }}
    >
      <h2
        id="metrobi-block-title"
        sx={{ variant: 'text.headingSm', margin: 0 }}
      >
        {title}
      </h2>
      <p
        id="metrobi-block-description"
        sx={{ variant: 'text.paragraphMdDefault', marginTop: '20px' }}
      >
        {mode === 'mark'
          ? `${
              singular ? 'This stop has' : 'These stops have'
            } been marked ineligible for Metrobi delivery. Remove ${
              singular ? 'it' : 'them'
            } from the route, or update ${
              singular ? 'its' : 'their'
            } eligibility, before marking this route as Metrobi.`
          : singular
          ? `${
              offenders[0].businessName || 'This location'
            } is flagged Metrobi Ineligible and can't be added to a Metrobi route.`
          : "These stops are flagged Metrobi Ineligible and can't be added to a Metrobi route."}
      </p>
      {(mode === 'mark' || !singular) && (
        <ul
          sx={{
            listStyle: 'none',
            margin: 0,
            padding: '10px 14px',
            backgroundColor: 'backgroundWeak',
            border: '1px solid',
            borderColor: 'backgroundMedium',
            borderRadius: '8px',
            variant: 'text.paragraphMdDefault',
          }}
        >
          {offenders.map((offender) => (
            <li key={offender.stopId} sx={{ paddingY: '4px' }}>
              {offender.businessName || 'Unknown location'}
              {offender.address && ` — ${offender.address}`}
            </li>
          ))}
        </ul>
      )}
      {mode === 'move' && (
        <p
          sx={{
            variant: 'text.paragraphSmDefault',
            color: 'secondaryText',
            marginBottom: 0,
          }}
        >
          Update {singular ? "the location's" : "the locations'"} eligibility
          status if you believe this is a mistake.
        </p>
      )}
      <div sx={{ ...buttonContainer, marginTop: '32px' }}>
        <Button variant="secondary" onPress={cancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
